import {getFieldTypeOcticon, toIssueFieldType} from '@github-ui/issue-fields-shared/issue-field'
import {IssueFieldSingleSelectValueToken} from '@github-ui/issue-metadata/IssueFieldSingleSelectValueToken'
import {Link} from '@primer/react'

import {createIssueEventExternalUrl} from '../utils/urls'
import type {IssueFieldDataType} from './__generated__/IssueFieldAddedEvent.graphql'
import styles from './IssueFieldEvent.module.css'
import type {TimelineRowEventActor$key} from './row/__generated__/TimelineRowEventActor.graphql'
import {TimelineRow} from './row/TimelineRow'

// Helper function to parse dates from the backend API
// The backend returns ISO 8601 timestamps (e.g., "2026-01-14T00:00:00.000Z")
// but we need to preserve the date portion regardless of the user's timezone.
function stripTimezone(isoString: string | null): Date | null {
  if (!isoString) return null

  // Validate ISO 8601 date format (YYYY-MM-DD at the start)
  const dateMatch = isoString.match(/^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})/)
  if (!dateMatch?.groups) return null

  const {year, month, day} = dateMatch.groups
  if (!year || !month || !day) return null

  const yearNum = parseInt(year, 10)
  const monthNum = parseInt(month, 10)
  const dayNum = parseInt(day, 10)

  // Validate numeric ranges
  if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum)) return null
  if (monthNum < 1 || monthNum > 12) return null
  if (dayNum < 1 || dayNum > 31) return null

  // Create a Date at noon UTC to avoid timezone issues
  // This ensures the date displays correctly in any timezone
  const date = new Date(Date.UTC(yearNum, monthNum - 1, dayNum, 12, 0, 0, 0))

  // Final validation: check if Date construction succeeded
  return isNaN(date.getTime()) ? null : date
}

export function getValue(dataType: string, newValue: string | undefined, newColor: string | null | undefined) {
  if (!newValue) {
    return null
  }
  if (dataType === 'SINGLE_SELECT') {
    return <IssueFieldSingleSelectValueToken name={newValue} color={newColor || ''} getTooltipText={() => undefined} />
  } else if (dataType === 'DATE') {
    // Parse the date and format it in UTC to show consistent calendar date
    const date = stripTimezone(newValue)
    if (!date) return 'Invalid Date'
    return date.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC'})
  }
  return newValue
}

export function getSearchLink(
  repositoryNameWithOwner: string | undefined,
  fieldName: string,
  fieldValue: string | undefined,
): string | undefined {
  if (!repositoryNameWithOwner || !fieldValue) {
    return undefined
  }

  const sanitizedFieldName = fieldName.replaceAll(' ', '-')
  const query = encodeURIComponent(`field.${sanitizedFieldName}:"${fieldValue}"`)
  return `/${repositoryNameWithOwner}/issues?q=${query}`
}

export type IssueFieldEventBaseProps = {
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string | null | undefined
  refAttribute?: React.RefObject<HTMLDivElement | null>
  repositoryNameWithOwner?: string
}

type IssueFieldEventProps = IssueFieldEventBaseProps & {
  fieldValue?: string | null | undefined
  dataType?: IssueFieldDataType
  fieldName?: string
  color?: string | null | undefined
  databaseId?: number | null | undefined
  actor: TimelineRowEventActor$key | null | undefined
  createdAt: string
  trailingLabel?: string
  leadingLabel?: string
  hasValue: boolean
  rollupContent?: React.ReactNode
}

export function IssueFieldEvent({
  fieldValue,
  dataType,
  fieldName,
  color,
  databaseId,
  actor,
  highlightedEventId,
  repositoryNameWithOwner,
  refAttribute,
  createdAt,
  issueUrl,
  onLinkClick,
  trailingLabel,
  leadingLabel,
  hasValue,
  rollupContent,
}: IssueFieldEventProps) {
  if (rollupContent) {
    const highlighted = String(databaseId) === highlightedEventId

    return (
      <TimelineRow
        highlighted={highlighted}
        refAttribute={refAttribute}
        actor={actor}
        createdAt={createdAt}
        deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
        onLinkClick={onLinkClick}
        leadingIcon={getFieldTypeOcticon(toIssueFieldType(dataType || 'TEXT'))}
      >
        <TimelineRow.Main>{rollupContent}</TimelineRow.Main>
      </TimelineRow>
    )
  }

  if ((hasValue && !fieldValue) || !dataType || !fieldName) {
    return null
  }

  const highlighted = String(databaseId) === highlightedEventId

  const value = hasValue ? getValue(dataType, fieldValue || undefined, color) : undefined
  const searchLink = getSearchLink(repositoryNameWithOwner, fieldName, fieldValue || undefined)

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={getFieldTypeOcticon(toIssueFieldType(dataType))}
    >
      <TimelineRow.Main>
        {leadingLabel}
        <div className={styles.issueFieldTokenWrapper}>{fieldName}</div>
        {trailingLabel}
        {value && (
          <div className={styles.issueFieldTokenWrapper}>
            <Link href={searchLink} inline className={styles.issueFieldValueLink}>
              {value}
            </Link>
          </div>
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}
